import { getToken, setToken } from './store';

export interface Env {
  key: string;
  label: string;
  short: string;
  apiBase: string;
  adminBase?: string;
  host: string;
  appPrefix: string;
}

type RuntimeConfig = { envs?: Env[] };
const RUNTIME: RuntimeConfig = (window as unknown as { __VS_ADMIN__?: RuntimeConfig }).__VS_ADMIN__ ?? {};

const DEV_ENVS: Env[] = [
  { key: 'local', label: 'Local', short: 'LOCAL', apiBase: '/_env/local', host: '127.0.0.1:8080', appPrefix: 'vocalstack-e2e-' },
  { key: 'e2e', label: 'E2E', short: 'E2E', apiBase: '/_env/e2e', host: 'api.e2e.vocalstack.com', appPrefix: 'vocalstack-e2e-' },
  { key: 'production', label: 'Production', short: 'PRODUCTION', apiBase: '/_env/production', adminBase: '/_env/admin-production', host: 'api.vocalstack.com', appPrefix: 'vocalstack-prd-' },
];

export const ENVS: Env[] = RUNTIME.envs?.length ? RUNTIME.envs : DEV_ENVS;
export const IS_LOCAL = !RUNTIME.envs?.length;

export function flyApp(env: Env, key: string): string {
  return key === 'app' && env.appPrefix === 'vocalstack-e2e-' ? 'vocalstack-e2e' : `${env.appPrefix}${key}`;
}

export function envByKey(key: string): Env {
  return ENVS.find((e) => e.key === key) ?? ENVS[0];
}

export class ApiErr extends Error {
  status: number;
  code: string;
  details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function parse(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return text; }
}

function toErr(res: Response, data: unknown): ApiErr {
  const d = data as { code?: string; message?: string; error?: { code?: string; message?: string }; data?: { details?: unknown } } | null;
  return new ApiErr(res.status, d?.code ?? d?.error?.code ?? `http_${res.status}`, d?.message ?? d?.error?.message ?? `the API answered ${res.status}`, d?.data?.details);
}

const AUTH_PATH = '/app/auth';

const refreshing = new Map<string, Promise<boolean>>();
export function refresh(env: Env): Promise<boolean> {
  let p = refreshing.get(env.key);
  if (!p) {
    p = (async () => {
      try {
        const res = await fetch(`${env.apiBase}${AUTH_PATH}/refresh`, { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: '{}' });
        if (!res.ok) return false;
        const body = (await res.json()) as { accessToken?: string };
        if (!body.accessToken) return false;
        setToken(env.key, body.accessToken);
        return true;
      } catch {
        return false;
      } finally {
        refreshing.delete(env.key);
      }
    })();
    refreshing.set(env.key, p);
  }
  return p;
}

export async function call<T = unknown>(env: Env, path: string, init: { method?: string; body?: unknown; form?: FormData; raw?: boolean } = {}): Promise<T> {
  const go = async (): Promise<Response> => {
    const token = getToken(env.key);
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (init.body !== undefined) headers['content-type'] = 'application/json';
    try {
      const base = path.startsWith('/admin') ? env.adminBase ?? env.apiBase : env.apiBase;
      const body = init.form ?? (init.body === undefined ? undefined : JSON.stringify(init.body));
      return await fetch(`${base}${path}`, { method: init.method ?? 'GET', headers, credentials: 'include', body });
    } catch {
      throw new ApiErr(0, 'unreachable', `${env.label}'s API did not answer`);
    }
  };
  let res = await go();
  if (res.status === 401 && (await refresh(env))) res = await go();
  if (init.raw) {
    if (!res.ok) throw toErr(res, await parse(res));
    return (await res.text()) as unknown as T;
  }
  const data = await parse(res);
  if (!res.ok) throw toErr(res, data);
  return data as T;
}

export const get = <T = unknown>(env: Env, path: string) => call<T>(env, path);
export const post = <T = unknown>(env: Env, path: string, body?: unknown) => call<T>(env, path, { method: 'POST', body: body ?? {} });
export const put = <T = unknown>(env: Env, path: string, body?: unknown) => call<T>(env, path, { method: 'PUT', body });
export const patch = <T = unknown>(env: Env, path: string, body?: unknown) => call<T>(env, path, { method: 'PATCH', body });
export const del = <T = unknown>(env: Env, path: string, body?: unknown) => call<T>(env, path, { method: 'DELETE', body });

interface TokenReply { accessToken?: string; pendingVerification?: boolean; email?: string; pendingAuthenticationToken?: string }
export interface PendingVerification { email: string; pendingToken: string }

async function authPost(env: Env, path: string, body: unknown): Promise<TokenReply> {
  let res: Response;
  try {
    res = await fetch(`${env.apiBase}${path}`, { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  } catch {
    throw new ApiErr(0, 'unreachable', `${env.label}'s API could not be reached.`);
  }
  const data = await parse(res);
  if (!res.ok) throw toErr(res, data);
  return data as TokenReply;
}

function take(env: Env, t: TokenReply, email: string): PendingVerification | null {
  if (t.accessToken) { setToken(env.key, t.accessToken); return null; }
  if (t.pendingVerification && t.pendingAuthenticationToken) return { email: t.email || email, pendingToken: t.pendingAuthenticationToken };
  throw new ApiErr(0, 'signin', 'sign-in failed');
}

export async function signIn(env: Env, email: string, password: string): Promise<PendingVerification | null> {
  return take(env, await authPost(env, `${AUTH_PATH}/password`, { email, password }), email);
}

export async function verifyEmail(env: Env, pending: PendingVerification, code: string): Promise<void> {
  const t = await authPost(env, `${AUTH_PATH}/verify-email`, { pendingAuthenticationToken: pending.pendingToken, code });
  if (!t.accessToken) throw new ApiErr(0, 'signin', 'verification failed');
  setToken(env.key, t.accessToken);
}

export function signOut(env: Env) {
  const token = getToken(env.key);
  setToken(env.key, null);
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  void fetch(`${env.apiBase}${AUTH_PATH}/logout`, { method: 'POST', credentials: 'include', headers }).catch(() => {});
}

export function secondsLeft(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const { exp } = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof exp === 'number' ? exp - Date.now() / 1000 : null;
  } catch {
    return null;
  }
}

export function keepSignedIn() {
  const tick = () => {
    for (const env of ENVS) {
      const t = getToken(env.key);
      const left = t ? secondsLeft(t) : null;
      if (left !== null && left < 60 && document.visibilityState === 'visible') void refresh(env);
    }
  };
  setInterval(tick, 20_000);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') tick(); });
  tick();
}

export interface AdminMe {
  admin: { workosUserId: string; email: string; name: string };
  env: string;
  appPrefix: string;
  fly: boolean;
  github: boolean;
  image?: string | null;
  schema?: AdminSchema;
}

export interface AdminMigration { version: number; name: string }
export interface AdminSchema {
  state: 'ok' | 'behind' | 'mismatch' | 'ahead' | 'unknown';
  readOnly: boolean;
  missing: AdminMigration[];
  unknown: AdminMigration[];
  checksumMismatch: AdminMigration[];
  binaryLatest?: AdminMigration | null;
  databaseLatest?: AdminMigration | null;
  error?: string;
  message: string;
}

export async function whoAmI(env: Env): Promise<{ me: AdminMe | null; reason?: string; reachable: boolean }> {
  if (!getToken(env.key)) return { me: null, reachable: true, reason: 'signed out' };
  try {
    return { me: await get<AdminMe>(env, '/admin/me'), reachable: true };
  } catch (e) {
    const err = e as ApiErr;
    if (err.status === 401) { setToken(env.key, null); return { me: null, reachable: true, reason: 'signed out' }; }
    if (err.status === 403) return { me: null, reachable: true, reason: err.message };
    if (err.status === 404) return { me: null, reachable: true, reason: `${env.label}'s API has no /admin routes yet: it runs a build from before the dashboard. Rebuild it (make build-dev) and start it again.` };
    return { me: null, reachable: false, reason: err.message };
  }
}

export function initials(name: string) {
  const parts = name.trim().split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? 'A') + (parts[1]?.[0] ?? '')).toUpperCase();
}

export const fmtBytes = (n: number) => (n >= 1e12 ? `${(n / 1e12).toFixed(2)} TB` : n >= 1e9 ? `${(n / 1e9).toFixed(2)} GB` : n >= 1e6 ? `${(n / 1e6).toFixed(1)} MB` : n >= 1e3 ? `${(n / 1e3).toFixed(0)} KB` : `${n} B`);
export const fmtDur = (s: number) => { s = Math.max(0, Math.floor(s)); if (s < 60) return `${s}s`; const m = Math.floor(s / 60); if (m < 60) return `${m}m`; const h = Math.floor(m / 60); if (h < 24) return `${h}h ${m % 60}m`; return `${Math.floor(h / 24)}d ${h % 24}h`; };
export const fmtMoney = (x: number) => `$${x.toFixed(2)}`;
export const fmtTime = (iso?: string | null) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—');
export const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
export const ago = (iso?: string | null) => { if (!iso) return '—'; const d = (Date.now() - new Date(iso).getTime()) / 1000; return d < 60 ? `${Math.floor(d)} s ago` : `${fmtDur(d)} ago`; };
export const shortDigest = (d?: string | null) => (d ? d.replace('sha256:', '').slice(0, 8) : '—');
